import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { funnelSteps } from '../components/FunnelQuestions';
import { leadsService } from '../services/leadsService';

export default function Funnel() {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contactInfo, setContactInfo] = useState({
    NAME: '', COMPANY: '', EMAIL: '', WEBSITE: ''
  });

  const handleOptionSelect = (stepId, value) => {
    setAnswers(prev => ({ ...prev, [stepId]: value }));
    setTimeout(() => {
      setCurrentStepIndex(prev => prev + 1);
    }, 400);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await leadsService.submitLead(answers, contactInfo);
      setIsComplete(true);
      // Auto-redirect to home page after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error("Submission error:", err);
      // Display the actual error message returned from Supabase, or a fallback
      setError(err.message || "Failed to submit information. Please check your database connection and RLS policies.");
    } finally {
      setLoading(false);
    }
  };

  const isLeadCapture = currentStepIndex === funnelSteps.length;

  return (
    <div className="min-h-screen bg-black flex flex-col items-center pt-40 px-8">
      <div className="stark-bg" />
      
      <div className="w-full max-w-4xl">
        <AnimatePresence mode="wait">
          {!isComplete ? (
            <motion.div
              key={currentStepIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5, ease: "circOut" }}
            >
              {!isLeadCapture ? (
                <>
                  <div className="flex items-center gap-4 mb-8">
                    <span className="text-stark-pink font-black text-sm tracking-widest uppercase">Step 0{currentStepIndex + 1}</span>
                    <div className="flex-1 h-[1px] bg-white/10" />
                  </div>
                  
                  <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-white mb-16 leading-tight">
                    {funnelSteps[currentStepIndex].title}
                  </h2>

                  <div className="grid md:grid-cols-2 gap-4">
                    {funnelSteps[currentStepIndex].options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleOptionSelect(funnelSteps[currentStepIndex].id, option.label)}
                        className="group flex items-center justify-between p-8 glass-card rounded-2xl hover:bg-stark-lime transition-all duration-500"
                      >
                        <div className="flex items-center gap-6">
                          <SafeIcon name={option.icon.replace('Fi', '')} className="text-3xl text-stark-lime group-hover:text-black transition-colors" />
                          <span className="text-xl font-bold group-hover:text-black">{option.label}</span>
                        </div>
                        <SafeIcon icon={FiIcons.FiArrowRight} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all text-black" />
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="max-w-2xl mx-auto">
                  <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-12">TELL US ABOUT THE PROJECT</h2>
                  
                  {error && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-8 p-6 glass-card border-red-500/30 bg-red-500/10 rounded-2xl flex items-start gap-4"
                    >
                      <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-400 text-2xl shrink-0 mt-1" />
                      <div>
                        <h3 className="text-red-400 font-black uppercase tracking-widest text-sm mb-1">Submission Failed</h3>
                        <p className="text-white/80 font-medium">{error}</p>
                      </div>
                    </motion.div>
                  )}

                  <form onSubmit={handleContactSubmit} className="space-y-8">
                    {['NAME', 'COMPANY', 'EMAIL', 'WEBSITE'].map((field) => (
                      <div key={field} className="relative group">
                        <input
                          required={field !== 'WEBSITE'}
                          type={field === 'EMAIL' ? 'email' : 'text'}
                          placeholder={field}
                          value={contactInfo[field]}
                          onChange={(e) => setContactInfo(prev => ({...prev, [field]: e.target.value}))}
                          className="w-full bg-transparent border-b-2 border-white/10 py-6 text-2xl font-bold focus:outline-none focus:border-stark-lime transition-colors placeholder:text-white/10"
                        />
                      </div>
                    ))}
                    
                    <button 
                      disabled={loading}
                      type="submit" 
                      className="w-full py-8 bg-stark-lime text-black font-black text-2xl rounded-full hover:bg-white transition-all duration-500 uppercase tracking-tighter mt-12 disabled:opacity-50 flex justify-center items-center gap-3"
                    >
                      {loading ? (
                        <>
                          <div className="w-6 h-6 border-2 border-black/20 border-t-stark-pink rounded-full animate-spin" />
                          PROCESSING...
                        </>
                      ) : 'SUBMIT'}
                    </button>
                  </form>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="w-24 h-24 bg-stark-pink rounded-full flex items-center justify-center mx-auto mb-12 shadow-[0_0_50px_rgba(255,0,127,0.3)]">
                <SafeIcon icon={FiIcons.FiCheck} className="text-white text-5xl" />
              </div>
              <h2 className="text-[8vw] md:text-[6rem] font-black uppercase tracking-tighter leading-none mb-8">THANX FOR THE INFORMATION.</h2>
              <p className="text-2xl text-white/40 mb-16 font-medium">The team will be in touch. Redirecting to home...</p>
              <button 
                onClick={() => navigate('/')}
                className="px-12 py-6 glass-card rounded-full font-black text-xl hover:bg-white hover:text-black transition-all"
              >
                RETURN HOME NOW
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}